import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { SearchIcon } from '../../../../components/ui';
import { ReportExportButtons } from '../../../../components/reports/ReportExportButtons';
import { Column } from '../../../../components/DataTable/DataTable';
import { useReportExport } from './useReportExport';

export interface AccountingReportShellProps<T extends Record<string, unknown>> {
  /** Full-width filter Card (Trial Balance toolbar style) */
  toolbar: React.ReactNode;
  run: boolean;
  loading?: boolean;
  rows: T[];
  columns: Column<T>[];
  entityName: string;
  exportFileBase: string;
  exportDateTo?: string;
  /** Left side of workspace header — e.g. period badge */
  statusBadge?: React.ReactNode;
  /** Shown next to export buttons — e.g. currency badge */
  statusExtra?: React.ReactNode;
  recordCount?: number;
  recordLabel?: string;
  readyTitle?: string;
  readyMessage?: string;
  loadingMessage?: string;
  children: React.ReactNode;
}

export function AccountingReportShell<T extends Record<string, unknown>>({
  toolbar,
  run,
  loading = false,
  rows,
  columns,
  entityName,
  exportFileBase,
  exportDateTo,
  statusBadge,
  statusExtra,
  recordCount,
  recordLabel = 'record',
  readyTitle = 'Ready to search',
  readyMessage = 'Set your filters, then click Search to run the report.',
  loadingMessage = 'Loading report…',
  children,
}: AccountingReportShellProps<T>) {
  const { colors } = useTheme();
  const { onExportExcel, onExportPDF, exportDisabled } = useReportExport(
    rows,
    columns,
    entityName,
    exportFileBase,
    exportDateTo,
  );

  const showExports = run && !loading && rows.length > 0;
  const statusText =
    run && !loading && recordCount != null
      ? `${recordCount} ${recordLabel}${recordCount === 1 ? '' : 's'}`
      : run && loading
        ? loadingMessage
        : readyMessage;

  return (
    <div className="flex flex-col h-full min-h-0 p-4 gap-3">
      {toolbar}

      <div
        className={cn(
          'flex flex-1 min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div
            className={cn(
              'flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b',
              colors.border,
              colors.surfaceHover,
            )}
          >
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              {run && statusBadge}
              <span className={cn('text-sm', colors.textMuted)}>{statusText}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {showExports && (
                <ReportExportButtons
                  onExportExcel={onExportExcel}
                  onExportPDF={onExportPDF}
                  disabled={exportDisabled}
                />
              )}
              {run && statusExtra}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-auto">
            {!run && (
              <div
                className={cn(
                  'flex flex-col items-center justify-center h-full min-h-[220px] px-8 text-center',
                  colors.textMuted,
                )}
              >
                <div
                  className={cn(
                    'h-12 w-12 rounded-full flex items-center justify-center mb-3',
                    colors.surfaceHover,
                  )}
                >
                  <SearchIcon className="h-5 w-5 opacity-60" />
                </div>
                <p className="text-sm font-medium">{readyTitle}</p>
                <p className="text-xs mt-1 max-w-sm leading-relaxed">{readyMessage}</p>
              </div>
            )}
            {run && loading && (
              <div
                className={cn(
                  'flex items-center justify-center h-full min-h-[200px] text-sm',
                  colors.textMuted,
                )}
              >
                {loadingMessage}
              </div>
            )}
            {run && !loading && children}
          </div>
        </div>
      </div>
    </div>
  );
}
